import React from 'react';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import ImageUploader from '../../../../composed/products/ImageUpLoader';
import { SectionHeader } from '../components/SectionHeader';
import { SectionInfoBanner } from '../components/SectionInfoBanner';
import { FieldRow } from '../components/ToggleCard';
import { StoreTabFooter } from '../components/StoreTabFooter';
import {
  SS_OUTLINED_INPUT,
  STORE_FRONT_IMAGE_FIELDS,
  YOUR_PICK_IMAGE_SECTION,
} from '../storeSettingsConstants';
import { Padding } from '@mui/icons-material';
import TextTransformation from '../../../../utils/textTransformation';

const DISPLAY_NAME_LEGAL_TOOLTIP =
  'This name is shown to customers on the platform. You must own the name or have permission to use any trademarks, trade names, or branding you enter. Misleading or unauthorized names may violate our terms and applicable law, and we may remove content or take other action.';

const displayNameFieldSx = {
  width: 'min(100%, 320px)',
  minWidth: 200,
  flexShrink: 0,
  '& .MuiInputBase-input': { fontSize: '14px', py: '8.5px' },
};

export function StoreProfileSection({
  meta,
  form,
  storeImageFile,
  setStoreImageFile,
  storeImagePreviewUrl,
  setStoreImagePreviewUrl,
  storeFrontImageFiles,
  setStoreFrontImageFile,
  storeFrontImagePreviewUrls,
  setStoreFrontImagePreviewUrl,
  updateField,
  updateStoreFrontImageUrl,
  tabFooterProps,
}) {
  return (
    <>
      <SectionHeader Icon={meta.Icon} title={meta.title} subtitle={meta.subtitle} />
      <SectionInfoBanner title='What this tab controls'>
        <>
          <a style={{ fontFamily: 'Inter Bold' }}> Heads up! </a>Uploading or changing your store
          image/logo will make your store temporarily unavailable. Your store will go live again
          after admin approval.
        </>
      </SectionInfoBanner>
      <FieldRow
        label='Display name'
        description={
          <span
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              // gap: 4,
            }}
          >
            <span style={{ minWidth: 0 }}>You must have legal rights to use this name.</span>
            <Tooltip
              title={DISPLAY_NAME_LEGAL_TOOLTIP}
              arrow
              placement='top'
              enterTouchDelay={0}
              sx={{ padding: '20px' }}
              slotProps={{
                tooltip: {
                  sx: {
                    backgroundColor: '#f3f4f6',
                    color: '#111111',
                    fontSize: '14px',
                    fontWeight: 500,
                    letterSpacing: '0.25px',
                    px: 1.5,
                    py: 1,
                    maxWidth: 360,
                    border: '1px solid #9ca3af',
                  },
                },
                arrow: {
                  sx: {
                    color: '#f3f4f6',
                  },
                },
              }}
            >
              <span style={{ display: 'inline-flex', flexShrink: 0, marginTop: -2 }}>
                <IconButton
                  size='small'
                  aria-label='Why legal rights matter for display name'
                  sx={{
                    p: 0.25,
                    color: '#6b7280',
                    '&:hover': { color: '#181436', backgroundColor: 'rgba(24, 20, 54, 0.06)' },
                  }}
                >
                  <InfoOutlined sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </span>
        }
      >
        <TextField
          id='display_name'
          className={SS_OUTLINED_INPUT}
          size='small'
          hiddenLabel
          placeholder='Name shown to customers'
          value={(form.display_name || '')}
          onChange={(e) => updateField('display_name', e.target.value)}
          inputProps={{ 'aria-label': 'Display name shown to customers' }}
          variant='outlined'
          sx={displayNameFieldSx}
        />
      </FieldRow>

      <div
        className='store-settings-logo-block'
        role='group'
        aria-labelledby='store-settings-logo-heading'
      >
        <div className='store-settings-logo-block__text'>
          <div id='store-settings-logo-heading' className='store-settings-logo-block__title'>
            Store logo
          </div>
          <p className='store-settings-logo-block__desc'>
            Displayed on your store profile. Accepted formats: PNG, JPG, SVG, GIF (max 2 MB). Click
            ‘Update’ to save changes.
          </p>
        </div>
        <div className='store-settings-logo-uploader-wrap'>
          <ImageUploader
            imageFor='store'
            existingImageUrl={
              !storeImagePreviewUrl && !storeImageFile ? (form.display_image || '').trim() : ''
            }
            onChange={(file) => {
              if (file === null) updateField('display_image', '');
            }}
            imageFile={storeImageFile}
            setImageFile={setStoreImageFile}
            previewUrl={storeImagePreviewUrl}
            setPreviewUrl={setStoreImagePreviewUrl}
          />
        </div>
      </div>

      <div
        className='store-settings-logo-block'
        role='group'
        aria-labelledby='store-settings-your-pick-heading'
      >
        <div className='store-settings-logo-block__text'>
          <div id='store-settings-your-pick-heading' className='store-settings-logo-block__title'>
            {YOUR_PICK_IMAGE_SECTION.title}
          </div>
          <p className='store-settings-logo-block__desc'>
            {YOUR_PICK_IMAGE_SECTION.description} Accepted formats: PNG, JPG, SVG, GIF (max 2 MB).
          </p>
        </div>
        <div
          className='store-settings-logo-uploader-wrap'
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: 16,
            width: '100%',
            maxWidth: 'none',
          }}
        >
          {YOUR_PICK_IMAGE_SECTION.fields.map((field) => (
            <div key={field.key} style={{ flex: '1 1 0', minWidth: 0 }}>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>{field.label}</div>
              <ImageUploader
                imageFor='store'
                size='100%'
                existingImageUrl={
                  !storeFrontImagePreviewUrls[field.key] && !storeFrontImageFiles[field.key]
                    ? (form.store_front_images?.[field.key] || '').trim()
                    : ''
                }
                onChange={(file) => {
                  if (file === null) updateStoreFrontImageUrl(field.key, '');
                }}
                imageFile={storeFrontImageFiles[field.key] || null}
                setImageFile={(file) => setStoreFrontImageFile(field.key, file)}
                previewUrl={storeFrontImagePreviewUrls[field.key] || ''}
                setPreviewUrl={(url) => setStoreFrontImagePreviewUrl(field.key, url)}
              />
            </div>
          ))}
        </div>
      </div>

      {STORE_FRONT_IMAGE_FIELDS.map((field) => (
        <div
          key={field.key}
          className='store-settings-logo-block'
          role='group'
          aria-labelledby={`store-settings-${field.key}-heading`}
        >
          <div className='store-settings-logo-block__text'>
            <div id={`store-settings-${field.key}-heading`} className='store-settings-logo-block__title'>
              {field.label}
            </div>
            <p className='store-settings-logo-block__desc'>
              {field.description} Accepted formats: PNG, JPG, SVG, GIF (max 2 MB).
            </p>
          </div>
          <div className='store-settings-logo-uploader-wrap'>
            <ImageUploader
              imageFor='store'
              existingImageUrl={
                !storeFrontImagePreviewUrls[field.key] && !storeFrontImageFiles[field.key]
                  ? (form.store_front_images?.[field.key] || '').trim()
                  : ''
              }
              onChange={(file) => {
                if (file === null) updateStoreFrontImageUrl(field.key, '');
              }}
              imageFile={storeFrontImageFiles[field.key] || null}
              setImageFile={(file) => setStoreFrontImageFile(field.key, file)}
              previewUrl={storeFrontImagePreviewUrls[field.key] || ''}
              setPreviewUrl={(url) => setStoreFrontImagePreviewUrl(field.key, url)}
            />
          </div>
        </div>
      ))}

      <StoreTabFooter tabId='1' {...tabFooterProps} />
    </>
  );
}
